import {
  IconAlertCircle,
  IconAlertTriangle,
  IconAt,
  IconCircleCheck,
  IconClipboardCheck,
  IconClipboardX,
  IconDownload,
  IconExclamationCircle,
  IconFileSearch,
  IconFolderSearch,
  IconHandStop,
  IconHeadphones,
  IconInfoCircle,
  IconMessageQuestion,
  IconPhoneCall,
  IconShieldCheck,
  IconUserQuestion,
} from '@tabler/icons-react'

// Every type that any producer can fire is listed here unconditionally.
// We used to gate rows on org feature flags (RTC, scoring, coaching,
// etc.) — but the producers don't consult those flags, so any gated-out
// row created a black hole: the manager could still receive the
// notification but had no UI to silence it. The org-flag check belongs
// on the producer side (or not at all); the settings page just lists
// what's deliverable.
// Each label mirrors the bell-row title set in workflow-service's
// `_TASK_TYPE_TO_TITLE` / `notify_users_of_note_mention`, in sentence
// case. Keep them aligned — a settings row a user can't recognize from
// the bell event they want to silence is a bug, not a style choice.
export const NOTIFICATION_CATEGORIES = [
  {
    key: 'coaching',
    label: 'Coaching',
    types: [
      { key: 'agent_requests_coaching', label: 'Agent requested coaching', icon: IconUserQuestion },
      { key: 'new_coaching_inbox_item', label: 'New coaching item', icon: IconClipboardCheck },
    ],
  },
  {
    key: 'quality',
    label: 'Quality',
    types: [
      { key: 'dispute', label: 'QA dispute filed', icon: IconAlertTriangle },
      { key: 'quality_inbox', label: 'New quality item', icon: IconFileSearch },
      { key: 'failed_criteria', label: 'Failed criterion', icon: IconExclamationCircle },
      { key: 'failed_scorecard', label: 'Failed scorecard', icon: IconClipboardX },
    ],
  },
  {
    key: 'realtime',
    label: 'Real-Time Event',
    types: [
      { key: 'raised_hand', label: 'Agent raised hand', icon: IconHandStop },
      { key: 'audio_issue', label: 'Audio issue', icon: IconHeadphones },
      // Opt-in (QCER-987): fires on every call start of a covered agent, so
      // the backend ships no default for it — this row is where it gets
      // turned on.
      { key: 'call_started', label: 'Call started', icon: IconPhoneCall },
      // Real-Time Alerts, split by the producer's category. The bell row's
      // title is the alert's own configured name; these labels describe the
      // category so a manager can silence a whole sentiment at once.
      { key: 'rta_alert_positive', label: 'Positive alert', icon: IconCircleCheck },
      { key: 'rta_alert_negative', label: 'Negative alert', icon: IconAlertCircle },
      { key: 'rta_alert_informative', label: 'Informative alert', icon: IconInfoCircle },
      {
        key: 'agent_gpt_no_answer',
        label: "AI Answers couldn't answer",
        icon: IconMessageQuestion,
      },
    ],
  },
  {
    key: 'compliance',
    label: 'Compliance',
    types: [
      { key: 'new_compliance_inbox_item', label: 'New compliance item', icon: IconShieldCheck },
    ],
  },
  {
    key: 'notes',
    label: 'Notes',
    types: [{ key: 'note_mention', label: 'You were mentioned in a note', icon: IconAt }],
  },
  {
    key: 'exports',
    label: 'Exports',
    types: [
      // The finish only. Starting an export is already answered by the toast
      // shown one click after the user asks for it, so reporting-service
      // emits this once, when the archive is ready or has failed.
      { key: 'call_asset_export', label: 'Call export ready', icon: IconDownload },
    ],
  },
  {
    key: 'knowledge_base',
    label: 'Knowledge base',
    types: [
      // Only the connection's creator gets this, once the folder scan finishes.
      { key: 'kb_scan_ready', label: 'Your files are ready to review', icon: IconFolderSearch },
    ],
  },
]

/** Categories the current user should see, given their LaunchDarkly flags. */
export const visibleNotificationCategories = (flags = {}) =>
  NOTIFICATION_CATEGORIES.filter((category) => !category.flag || !!flags[category.flag])

export const ALL_NOTIFICATION_TYPES = NOTIFICATION_CATEGORIES.flatMap((category) => category.types)

// Bell-row icon per type, derived from the categories so a new type can't be
// added without one. NotificationItem falls back to IconBell for unknown types.
export const NOTIFICATION_ICONS = Object.fromEntries(
  ALL_NOTIFICATION_TYPES.map((type) => [type.key, type.icon])
)
